"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/lib/auth";
import { getSharingStatus } from "@/lib/universityLink/api";
import type { SharingStatus } from "@/lib/universityLink/types";

// 学生から見た大学連携の状態（名簿に一致するか・共有中か）を1回だけ取得して保持する（#1861）。
//
// 「大学連携」のナビ項目は AppSidebar / Header / BottomNav の3箇所から同じ描画で呼ばれ、
// 大学連携の画面も同じ値を使う。実行中の取得と結果をモジュール変数で共有し、
// 初回に3回叩いたり、画面遷移のたびに取り直したりしない。名簿の有無はセッション中に変わらない。
//
// 共有の開始・停止をした側は applySharingStatus で保持値を更新する。取り直さなくても
// ナビと画面が同じ値を見られるようにするため。

type Entry = {
  userId: string;
  status: SharingStatus | null;
  failed: boolean;
};

let cached: Entry | null = null;
let inFlight: { userId: string; promise: Promise<void> } | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Entry | null {
  return cached;
}

// SSR とハイドレーション時は未取得として扱う（ナビ項目はクライアントで判定が済んでから出す）。
function getServerSnapshot(): Entry | null {
  return null;
}

function load(userId: string) {
  if (inFlight?.userId === userId) return inFlight.promise;

  const promise = getSharingStatus()
    .then((status) => {
      cached = { userId, status, failed: false };
    })
    .catch(() => {
      cached = { userId, status: null, failed: true };
    })
    .finally(() => {
      if (inFlight?.userId === userId) inFlight = null;
      emit();
    });
  inFlight = { userId, promise };
  return promise;
}

/**
 * 共有の開始・停止に成功した側が呼ぶ。保持値を差し替えて全購読者へ知らせる。
 * 保持値が無いとき（取得前・取得失敗）は何もしない。結果共有カードは保持値がある場合にだけ
 * 描画されるため、切り替えの操作がここへ来るときは保持値がある。
 */
export function applySharingStatus(status: SharingStatus) {
  if (!cached) return;
  cached = { ...cached, status, failed: false };
  emit();
}

/** テスト用。描画をまたいで保持値を持ち越さないようにする。 */
export function resetUniversityLinkCache() {
  cached = null;
  inFlight = null;
}

export interface UseUniversityLinkStatusResult {
  /** 取得できた共有状態。未ログイン・取得前・取得失敗のときは null。 */
  status: SharingStatus | null;
  /** ログイン済みで、まだ結果が無い間 true。 */
  loading: boolean;
  /** 取得に失敗した。reload で取り直せる。 */
  failed: boolean;
  reload: () => void;
}

export function useUniversityLinkStatus(): UseUniversityLinkStatusResult {
  const { user } = useAuth();
  const userId = user?.id;
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    if (!userId) return;
    if (cached?.userId === userId) return;
    void load(userId);
  }, [userId]);

  const reload = useCallback(() => {
    if (!userId) return;
    cached = null;
    inFlight = null;
    emit();
    void load(userId);
  }, [userId]);

  // 別のユーザーの値が残っていても使わない（アカウント切り替え直後）。
  const entry = userId && snapshot?.userId === userId ? snapshot : null;

  return {
    status: entry?.status ?? null,
    loading: !!userId && entry === null,
    failed: entry?.failed ?? false,
    reload,
  };
}

/**
 * 「大学連携」をナビに出すか。
 *
 * 名簿に一致する（eligible）か、複数の大学に一致して案内が必要（ambiguous）なときだけ出す。
 * 未ログイン・取得前・取得失敗のときは出さない。判定が済む前に一瞬でも出すと、
 * 招待のないユーザーに大学連携の項目が見えてしまう。
 */
export function useUniversityLinkVisible(): boolean {
  const { status } = useUniversityLinkStatus();
  if (!status) return false;
  return status.eligible || !!status.ambiguous;
}
